import { startOfDay } from "@app/core";
import { useCallback, useEffect, useState } from "react";
import type { CalendarView } from "../../stores/settings.store.js";
import { useCalendarFocusStore, useSettingsStore } from "../../stores/index.js";
import { useCalendarNavStore } from "../../stores/calendarNav.store.js";

/**
 * Shared wiring for every calendar route:
 *  1. Record this as the last-used calendar view (drives the Calendar nav tab +
 *     search "View" so both return to where the user left off).
 *  2. Consume a pending focus jump (a search "View" click parks the target date
 *     in the calendar-focus store) by moving the route's anchor to it.
 *
 * `onFocusDate` must be stable (useState setter or a useCallback).
 */
export function useCalendarNav(view: CalendarView, onFocusDate: (d: Date) => void) {
  const anchorDate = useCalendarFocusStore((s) => s.anchorDate);
  const clearAnchor = useCalendarFocusStore((s) => s.clearAnchor);
  const setLastCalendarView = useSettingsStore((s) => s.setLastCalendarView);

  useEffect(() => { void setLastCalendarView(view); }, [view, setLastCalendarView]);

  useEffect(() => {
    if (anchorDate) { onFocusDate(new Date(anchorDate)); clearAnchor(); }
  }, [anchorDate, clearAnchor, onFocusDate]);
}

export function useRememberedCalendarAnchor(
  view: CalendarView,
  fallback: () => Date,
  normalize: (d: Date) => Date = identityDate,
) {
  const rememberAnchor = useCalendarNavStore((s) => s.rememberAnchor);
  const [anchor, setAnchorState] = useState(() => {
    const saved = useCalendarNavStore.getState().getAnchor(view);
    return normalize(saved ?? fallback());
  });

  const setAnchor = useCallback((next: Date | ((prev: Date) => Date)) => {
    setAnchorState((prev) => {
      const resolved = normalize(typeof next === "function" ? next(prev) : next);
      rememberAnchor(view, resolved);
      return resolved;
    });
  }, [normalize, rememberAnchor, view]);

  // Day-granular shift that SKIPS the view's normalize (e.g. week's
  // startOfWeek). A horizontal finger-drag must move one day at a time — if the
  // anchor were re-snapped to the week boundary, forward shifts within a week
  // would be eaten (so the drag only ever moved backward, a whole week at a
  // time). `startOfDay` keeps the time clean without re-aligning the date.
  const setAnchorRaw = useCallback((next: Date | ((prev: Date) => Date)) => {
    setAnchorState((prev) => {
      const resolved = startOfDay(typeof next === "function" ? next(prev) : next);
      rememberAnchor(view, resolved);
      return resolved;
    });
  }, [rememberAnchor, view]);

  return [anchor, setAnchor, setAnchorRaw] as const;
}

function identityDate(d: Date): Date {
  return d;
}
